import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { useIntegratorMe } from "@/lib/me-query";
import { appsVisibleToUser } from "@/lib/subapp-access";
import { fetchWikiOAuthStatus, isDevGatewayUiOrigin, startWikiOAuth } from "@/lib/wiki";

function hasOutlineCallbackMessage(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has("outline") || params.has("outlineError");
}

/**
 * Auto-connect Outline only on the DEV primary hub (`5173/5175/` or `:5175`) or in production.
 * Aux branch mounts (`5173/5177/`, …) must not kick getOutline — they share the primary
 * API connection (status checked against 8123) after the primary hub has connected.
 */
export function WikiOAuthBootstrap() {
  const location = useLocation();
  const { data: me } = useIntegratorMe();
  const startedRef = useRef(false);
  const allowAutoStart = !import.meta.env.DEV || isDevGatewayUiOrigin();
  const hasWikiAccess = useMemo(
    () => appsVisibleToUser(me).some((application) => application.key === "wiki"),
    [me],
  );

  const statusQuery = useQuery({
    queryKey: ["wiki", "oauth-status", me?.user.id, allowAutoStart ? "gateway" : "aux"],
    queryFn: fetchWikiOAuthStatus,
    enabled:
      hasWikiAccess &&
      location.pathname.startsWith("/wiki") &&
      !hasOutlineCallbackMessage(location.search),
    retry: false,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!allowAutoStart) {
      return;
    }
    if (!hasWikiAccess || startedRef.current || hasOutlineCallbackMessage(location.search)) {
      return;
    }
    if (!location.pathname.startsWith("/wiki")) {
      return;
    }
    const status = statusQuery.data;
    if (!status || !status.configured || status.connected) {
      return;
    }
    startedRef.current = true;
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    void startWikiOAuth(returnTo)
      .then((response) => {
        window.location.assign(response.authorizeUrl);
      })
      .catch(() => {
        startedRef.current = false;
      });
  }, [
    allowAutoStart,
    hasWikiAccess,
    location.hash,
    location.pathname,
    location.search,
    statusQuery.data,
  ]);

  return null;
}
